import { useEffect, useMemo, useRef, useState } from "react";
import { Card, EmptyState } from "@pericles/framework-frontend";

import { promoteDiscoveryMessageToNote, sendDiscoveryChatMessage } from "../api/discoveryChatClient";
import type { DiscoveryChatMessage } from "../types/discoveryChat";

import { InlineAlert } from "./InlineAlert";

interface ProjectDiscoveryConversationsPanelProps {
  projectId: string;
  onNotesUpdated?: () => void;
}

function enrichMessages(messages: DiscoveryChatMessage[]): DiscoveryChatMessage[] {
  const timestamp = new Date().toISOString();
  return messages.map((message, index) => ({
    ...message,
    id: message.id ?? `${message.role}-${index}-${timestamp}`,
    createdAt: message.createdAt ?? timestamp,
  }));
}

export function ProjectDiscoveryConversationsPanel({
  projectId,
  onNotesUpdated,
}: ProjectDiscoveryConversationsPanelProps): JSX.Element {
  const [messages, setMessages] = useState<DiscoveryChatMessage[]>([]);
  const [input, setInput] = useState<string>("");
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isPromoting, setIsPromoting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [promotionNotice, setPromotionNotice] = useState<string | null>(null);
  const messageListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMessages([]);
    setInput("");
    setError(null);
  }, [projectId]);

  useEffect(() => {
    if (!messageListRef.current) return;
    messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
  }, [messages]);

  const orderedMessages = useMemo(() => {
    return messages.slice().sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return aTime - bTime;
    });
  }, [messages]);

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!projectId || !input.trim()) return;

    setIsSending(true);
    setError(null);

    try {
      const result = await sendDiscoveryChatMessage(projectId, input.trim(), orderedMessages);
      setMessages(enrichMessages(result.history));
      setInput("");
    } catch (err) {
      const messageText = err instanceof Error ? err.message : "Failed to send message";
      setError(messageText);
    } finally {
      setIsSending(false);
    }
  };

  const handlePromote = async (message: DiscoveryChatMessage) => {
    if (!projectId || !message.content) return;

    setIsPromoting(message.id ?? message.content);
    setPromotionNotice(null);
    setError(null);

    try {
      await promoteDiscoveryMessageToNote(projectId, message.content, message.id);
      setPromotionNotice("Message promoted to Discovery notes");
      onNotesUpdated?.();
    } catch (err) {
      const messageText = err instanceof Error ? err.message : "Failed to promote message";
      setError(messageText);
    } finally {
      setIsPromoting(null);
    }
  };

  return (
    <Card title="Conversations IA">
      <div className="flex flex-col gap-3 p-3">
        <p className="text-sm text-slate-700">
          Posez vos questions en lien avec la discovery (documents, réunions, risques). Les réponses de l'agent peuvent
          être promues en note pour enrichir la section Notes Discovery.
        </p>

        <div aria-live="polite" className="space-y-2">
          {error ? <InlineAlert tone="danger" title={error} /> : null}
          {promotionNotice ? <InlineAlert tone="success" title={promotionNotice} /> : null}
        </div>

        <div
          ref={messageListRef}
          className="max-h-[420px] min-h-[240px] overflow-y-auto rounded border border-slate-200 bg-white p-3"
        >
          {orderedMessages.length === 0 ? (
            <EmptyState
              title="Démarrez la conversation"
              description="Posez vos questions liées à la Discovery : documents, réunions, risques ou enjeux."
            />
          ) : (
            <div className="flex flex-col gap-3">
              {orderedMessages.map((message) => (
                <div
                  key={message.id}
                  className={`flex flex-col gap-1 rounded border px-3 py-2 shadow-sm ${
                    message.role === "assistant"
                      ? "border-indigo-100 bg-indigo-50/50"
                      : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                      {message.role}
                    </span>
                    {message.createdAt ? (
                      <span className="text-xs text-slate-500">
                        {new Date(message.createdAt).toLocaleString()}
                      </span>
                    ) : null}
                  </div>
                  <div className="whitespace-pre-line text-sm text-slate-800">{message.content}</div>
                  {message.role === "assistant" ? (
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <button
                        type="button"
                        className="button button-secondary button-xs"
                        onClick={() => void handlePromote(message)}
                        disabled={isPromoting === (message.id ?? message.content)}
                      >
                        {isPromoting === (message.id ?? message.content) ? "Promotion…" : "Promote to note"}
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        <form className="space-y-2" onSubmit={(event: React.FormEvent<HTMLFormElement>) => void handleSend(event)}>
          <label className="flex flex-col gap-2 text-sm text-slate-700">
            <span>Message</span>
            <textarea
              className="input min-h-[96px]"
              placeholder="Demandez une synthèse des risques, contraintes ou décisions"
              value={input}
              onChange={(event: any) => setInput(event.target.value)}
              disabled={isSending}
            />
          </label>

          <div className="flex flex-wrap items-center gap-2">
            <button type="submit" className="button button-primary" disabled={isSending || !input.trim()}>
              {isSending ? "Envoi…" : "Envoyer"}
            </button>
            <span className="text-xs text-slate-500">
              Les messages sont envoyés avec le contexte Discovery (documents, réunions, risques, contraintes).
            </span>
          </div>
        </form>
      </div>
    </Card>
  );
}
